import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, LoginCredentials, RegisterData, AuthResponse } from '../services/authService';
import { clearAuthTokens, persistTokens } from '../services/authTokens';
import { BFF_LOGOUT_PATH, MAMBA_AUTH_CUTOVER } from '../services/authMode';

interface AuthContextType {
  user: User | null;
  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
  /** Signed in at the IdP, but refused here: no account, no access, or an outage. */
  accessError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (MAMBA_AUTH_CUTOVER) {
        // There is nothing in the browser to inspect: the session lives in a
        // cookie the page cannot read. So "am I signed in?" is a question only
        // the server can answer, and a 401 here is the answer "no" rather than
        // an error worth reporting.
        try {
          setUser(await authService.probeProfile());
        } catch (err) {
          setUser(null);
          // Anything but a 401 means the person is signed in and refused. Sending
          // them to the login page would bounce straight back through Keycloak's
          // live session, forever, so it is shown rather than retried.
          const status = (err as { status?: number }).status;
          if (status !== 401) {
            setAccessError(err instanceof Error ? err.message : 'Unable to verify your access.');
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!accessToken || !refreshToken) {
        setLoading(false);
        return;
      }

      try {
        const userData = await authService.getProfile();
        setUser(userData);
      } catch {
        clearAuthTokens();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await authService.login(credentials);
    persistTokens({ access: response.access, refresh: response.refresh });
    setUser(response.user);
    return response;
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await authService.register(data);
      persistTokens({ access: response.access, refresh: response.refresh });
      setUser(response.user);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    if (MAMBA_AUTH_CUTOVER) {
      // A full navigation, not a fetch. Signing out has to end the session at
      // Keycloak as well as here (RP-initiated logout), and that is a redirect
      // chain the browser must follow — an XHR would end our session only and
      // leave the IdP ready to sign the person straight back in.
      clearAuthTokens();
      setUser(null);
      window.location.href = BFF_LOGOUT_PATH;
      return;
    }
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthTokens();
      setUser(null);
    }
  };

  const refreshUser = async () => {
    if (!MAMBA_AUTH_CUTOVER && !localStorage.getItem('accessToken')) return;
    const userData = await authService.getProfile();
    setUser(userData);
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    refreshUser,
    loading,
    isAuthenticated: !!user,
    accessError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
